import React, { useEffect } from "react";
import styles from "./CompanyDetails.module.scss";
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Radio,
  Row,
  Select,
  Switch,
  Tooltip,
  Typography,
} from "antd";
import {
  companyDetails,
  updateCompanyDetails,
  userProfile,
} from "../../services/authService";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  businessModels,
  employeeRanges,
  financialHealth,
  fundingStages,
  fundingTypes,
  industries,
  ipoStatus,
  legalStructures,
  marketPosition,
  queryKeys,
  revenueBrackets,
} from "../../constants";
import classNames from "classnames";
import UploadImage from "../../components/ImageUpload/UploadImage";
const { TextArea } = Input;

const { Text, Title } = Typography;

const useParamsQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const CompanyDetails = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const query = useParamsQuery();
  const editCompanyDetails = query.get("edit");
  const [advancedOptions, setAdvancedOptions] = React.useState(false);

  const onChange = (checked) => {
    setAdvancedOptions(checked);
  };

  const getCompanyDetailsQuery = useQuery({
    queryKey: [queryKeys.companyDetails],
    queryFn: userProfile,
    enabled: !!editCompanyDetails,
  });

  const updateUserCompanyMutation = useMutation({
    mutationFn: companyDetails,
    onSuccess: () => {
      navigate("/home", { replace: true });
      message.success("Company details added successfully");
    },
    onError: (error) => {
      message.error(error.detail || "Failed to update company details");
    },
  });

  const updateCompanyMutation = useMutation({
    mutationFn: updateCompanyDetails,
    onError: (error) => {
      message.error(error.detail || "Failed to update company details");
    },
  });

  const onFinish = (values) => {
    values.has_advance_settings = advancedOptions;
    if (editCompanyDetails) {
      updateCompanyMutation.mutate(values, {
        onSuccess: () => {
          navigate("/home/profile", { replace: true });
          message.success("Company details updated successfully");
        },
      });
      return;
    }
    updateUserCompanyMutation.mutate(values);
  };

  const handleUploadImage = (base64Image) => {
    const formValues = form.getFieldsValue(["name", "business_models"]);
    updateCompanyMutation.mutate(
      {
        ...formValues,
        logo: base64Image,
      },
      {
        onSuccess: () => {
          message.success("Company logo updated successfully");
          getCompanyDetailsQuery.refetch();
        },
      }
    );
  };

  useEffect(() => {
    if (getCompanyDetailsQuery?.data?.data && editCompanyDetails) {
      const { company } = getCompanyDetailsQuery.data.data;
      setAdvancedOptions(company?.has_advance_settings || false);
      form.setFieldsValue(company);
    }
  }, [getCompanyDetailsQuery?.data, form, editCompanyDetails]);

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div style={{ minHeight: "102px" }}>
            <UploadImage
              profile_picture={
                getCompanyDetailsQuery?.data?.data?.company?.logo ?? ""
              }
              handleUploadImage={handleUploadImage}
            />
          </div>
          <Title className={styles.title}>
            {editCompanyDetails ? "Update Company Details" : "Company Profile"}
          </Title>
          <Text className={styles.text}>
            {editCompanyDetails
              ? "Update your company details"
              : "Welcome back to Radar+! Please provide your company details to proceed with the application."}
          </Text>
        </div>
        <Form
          name="company-details"
          onFinish={onFinish}
          layout="vertical"
          requiredMark="optional"
          form={form}
          initialValues={{
            presence: "GLOBAL",
            revenue_brackets: null,
            annual_growth_rate: "",
            legal_structure: null,
            ipo_status: null,
            funding_stage: null,
            funding_type: null,
            supplier_website_links: "",
            key_competitor_website_links: "",
            merges_acquisitions_website_links: "",
            business_challenge_description: "",
          }}
        >
          <Row gutter={8}>
            <Col sm={24} md={12}>
              <Form.Item
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Please input your company name!",
                  },
                ]}
              >
                <Input placeholder="Company name" />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                name="business_models"
                rules={[
                  {
                    required: true,
                    message: "Please select your business model!",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  showSearch
                  allowClear
                  placeholder="Select a business model"
                  optionFilterProp="label"
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? "")
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? "").toLowerCase())
                  }
                  options={businessModels}
                />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                name="industry"
                rules={[
                  {
                    required: true,
                    message: "Please input your industry!",
                  },
                ]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select a industry"
                  optionFilterProp="label"
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? "")
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? "").toLowerCase())
                  }
                  options={industries}
                />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                name="employee_count"
                rules={[
                  {
                    required: true,
                    message: "Please select your employee range!",
                  },
                ]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select a employee range"
                  optionFilterProp="label"
                  options={employeeRanges}
                />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                name="financial_health"
                rules={[
                  {
                    required: true,
                    message: "Please select your financial health!",
                  },
                ]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select financial health"
                  optionFilterProp="label"
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? "")
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? "").toLowerCase())
                  }
                  options={financialHealth}
                />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                name="market_position"
                rules={[
                  {
                    required: true,
                    message: "Please select your market position!",
                  },
                ]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select market position"
                  optionFilterProp="label"
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? "")
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? "").toLowerCase())
                  }
                  options={marketPosition}
                />
              </Form.Item>
            </Col>
            <Col sm={24} md={24}>
              <Form.Item
                name="geographic_presence"
                rules={[
                  {
                    required: true,
                    message:
                      "Please input your address with city, state, country!",
                  },
                ]}
              >
                <TextArea
                  placeholder="Company Address e.g Angelic Highway, New Artie, United States"
                  rows={4}
                />
              </Form.Item>
            </Col>

            <Col sm={24} md={24}>
              <div className={styles.advancedContainer}>
                <Title className={styles.subSectionTitle}>
                  Advanced Business Details (Optional)
                </Title>
                <Tooltip title="Enable / Disable Advanced Details">
                  <Switch value={advancedOptions} onChange={onChange} />
                </Tooltip>
              </div>
            </Col>
          </Row>

          <Row
            gutter={8}
            className={classNames(styles.enableSection, {
              [styles.disableSection]: !advancedOptions,
            })}
          >
            <Col sm={24} md={12}>
              <Form.Item name="presence">
                <Radio.Group>
                  <Radio value={"GLOBAL"}>Global Presence</Radio>
                  <Radio value={"LOCAL"}>Local Presence</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item name="revenue_brackets">
                <Select
                  showSearch
                  allowClear
                  placeholder="Select revenue bracket"
                  optionFilterProp="label"
                  options={revenueBrackets}
                />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item name="annual_growth_rate">
                <Input
                  placeholder="Annual Growth / Decline Rate"
                  type="number"
                />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item name="legal_structure">
                <Select
                  showSearch
                  allowClear
                  placeholder="Select legal structure"
                  optionFilterProp="label"
                  options={legalStructures}
                />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item name="ipo_status">
                <Select
                  showSearch
                  allowClear
                  placeholder="Select IPO status"
                  optionFilterProp="label"
                  options={ipoStatus}
                />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item name="funding_stage">
                <Select
                  showSearch
                  allowClear
                  placeholder="Select funding stage"
                  optionFilterProp="label"
                  options={fundingStages}
                />
              </Form.Item>
            </Col>
            <Col sm={24} md={24}>
              <Form.Item name="funding_type">
                <Select
                  showSearch
                  allowClear
                  placeholder="Select funding type"
                  optionFilterProp="label"
                  options={fundingTypes}
                />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item name="supplier_website_links">
                <TextArea placeholder="Supplier Website Links" rows={3} />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item name="key_competitor_website_links">
                <TextArea
                  placeholder="Key Competitor's Website Links"
                  rows={3}
                />
              </Form.Item>
            </Col>
            <Col sm={24} md={24}>
              <Form.Item name="merges_acquisitions_website_links">
                <TextArea
                  placeholder="Mergers or Acquisitions Website Links"
                  rows={3}
                />
              </Form.Item>
            </Col>
            <Col sm={24} md={24}>
              <Form.Item name="business_challenge_description">
                <TextArea
                  placeholder="Business Challenges / Bottlenecks field for description"
                  rows={4}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: "0px" }}>
            <Button
              block
              type="primary"
              htmlType="submit"
              loading={updateUserCompanyMutation.isLoading}
            >
              {editCompanyDetails ? "Update" : "Proceed to Dashboard"}
            </Button>
            {editCompanyDetails && (
              <Button
                block
                onClick={() => navigate("/home/profile")}
                style={{ marginTop: 10 }}
              >
                Cancel
              </Button>
            )}
          </Form.Item>
        </Form>
      </div>
    </section>
  );
};
export default CompanyDetails;
